// src/components/admin/CollaboratorsManagement.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const CollaboratorsManagement = () => {
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [currentItem, setCurrentItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [stats, setStats] = useState({ total: 0, active: 0, unique_types: 0, countries: 0 });

  const [formData, setFormData] = useState({
    organization_name: '',
    logo_image: '',
    country: '',
    region: '',
    collaboration_type: 'Partner',
    contact_email: '',
    website_url: '',
    description: '',
    collaboration_area: '',
    start_date: '',
    is_active: true,
    display_order: 0
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const collaborationTypes = ['Partner', 'Sponsor', 'Affiliate', 'Donor', 'Academic', 'Research', 'Government', 'NGO', 'Other'];

  useEffect(() => {
    fetchData();
    fetchStats();
  }, [searchTerm, filterType, filterCountry]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('adminToken');

      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      let url = `${API_BASE}/admin/collaborators?`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
      if (filterType) url += `type=${encodeURIComponent(filterType)}&`;
      if (filterCountry) url += `country=${encodeURIComponent(filterCountry)}&`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      if (data.success) {
        setCollaborators(data.data.collaborators || []);
      } else {
        toast.error(data.message || 'Failed to fetch collaborators');
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Failed to fetch collaborators');
    } finally {
      if (loading) setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/admin/collaborators/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setStats(data.data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setCurrentItem(null);
    setFormData({
      organization_name: '', logo_image: '', country: '', region: '',
      collaboration_type: 'Partner', contact_email: '', website_url: '',
      description: '', collaboration_area: '', start_date: '',
      is_active: true, display_order: 0
    });
    setLogoFile(null);
    setLogoPreview(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setModalMode('edit');
    setCurrentItem(item);
    setFormData({
      organization_name: item.organization_name || '',
      logo_image: item.logo_image || '',
      country: item.country || '',
      region: item.region || '',
      collaboration_type: item.collaboration_type || 'Partner',
      contact_email: item.contact_email || '',
      website_url: item.website_url || '',
      description: item.description || '',
      collaboration_area: item.collaboration_area || '',
      start_date: item.start_date ? item.start_date.substring(0, 10) : '',
      is_active: item.is_active === 1,
      display_order: item.display_order || 0
    });
    setLogoFile(null);
    setLogoPreview(item.logo_image || null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/admin/collaborators/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        fetchData();
        fetchStats();
        toast.success('Collaborator deleted successfully');
      } else {
        toast.error(data.message || 'Failed to delete collaborator');
      }
    } catch (err) {
      toast.error('Failed to delete collaborator');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Upload logo first if a new file was selected
      const logoPath = await uploadLogo();

      const dataToSubmit = {
        ...formData,
        logo_image: logoPath
      };

      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const url = modalMode === 'create'
        ? `${API_BASE}/admin/collaborators`
        : `${API_BASE}/admin/collaborators/${currentItem.id}`;

      const response = await fetch(url, {
        method: modalMode === 'create' ? 'POST' : 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSubmit)
      });

      const data = await response.json();
      if (data.success) {
        setShowModal(false);
        fetchData();
        fetchStats();
        toast.success(`Collaborator ${modalMode === 'create' ? 'created' : 'updated'} successfully`);
      } else {
        toast.error(data.message || 'Operation failed');
      }
    } catch (err) {
      toast.error('Operation failed');
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setLogoFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return formData.logo_image;

    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }

      const uploadFormData = new FormData();
      uploadFormData.append('logo', logoFile);

      const response = await fetch(`${API_BASE}/admin-upload/collaborators/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });

      const data = await response.json();
      if (data.success) {
        return data.data.logoPath;
      } else {
        toast.error(data.message || 'Failed to upload logo');
        return formData.logo_image;
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload logo');
      return formData.logo_image;
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading && collaborators.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border" style={{ color: '#6B8E23' }} role="status"></div>
        <span className="ms-3 text-muted">Loading...</span>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="h3 fw-bold mb-1" style={{ color: '#1f2937' }}>Collaborators Management</h2>
          <p className="text-muted mb-0">Manage organizational partners and collaborators</p>
        </div>
        <button onClick={handleCreate} className="btn fw-bold shadow-sm" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
          <i className="bi bi-plus-circle me-2"></i>Add Collaborator
        </button>
      </div>

      {/* Stats */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #6B8E23' }}>
            <div className="card-body">
              <small className="text-muted">Total</small>
              <h4 className="fw-bold mb-0" style={{ color: '#6B8E23' }}>{stats.total || 0}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #4CAF50' }}>
            <div className="card-body">
              <small className="text-muted">Active</small>
              <h4 className="fw-bold mb-0" style={{ color: '#4CAF50' }}>{stats.active || 0}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #2196F3' }}>
            <div className="card-body">
              <small className="text-muted">Types</small>
              <h4 className="fw-bold mb-0" style={{ color: '#2196F3' }}>{stats.unique_types || 0}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #FF9800' }}>
            <div className="card-body">
              <small className="text-muted">Countries</small>
              <h4 className="fw-bold mb-0" style={{ color: '#FF9800' }}>{stats.countries || 0}</h4>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <input type="text" className="form-control" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
            <div className="col-md-4">
              <select className="form-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                <option value="">All Types</option>
                {collaborationTypes.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <input type="text" className="form-control" placeholder="Filter by country..." value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th className="px-4 py-3">Organization</th>
                <th className="py-3">Type</th>
                <th className="py-3">Country</th>
                <th className="py-3">Email</th>
                <th className="py-3">Status</th>
                <th className="py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {collaborators.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-5 text-muted">No collaborators found</td></tr>
              ) : (
                collaborators.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-3">
                      <div className="d-flex align-items-center">
                        {item.logo_image ? (
                          <img
                            src={item.logo_image.startsWith('http') ? item.logo_image : `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${item.logo_image}`}
                            alt={item.organization_name}
                            className="me-2"
                            style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        ) : (
                          <div className="bg-light rounded me-2 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                            <i className="bi bi-building"></i>
                          </div>
                        )}
                        <span className="fw-medium">{item.organization_name}</span>
                      </div>
                    </td>
                    <td className="py-3"><span className="badge" style={{ backgroundColor: '#6B8E2320', color: '#6B8E23' }}>{item.collaboration_type}</span></td>
                    <td className="py-3">{item.country || '—'}</td>
                    <td className="py-3">{item.contact_email || '—'}</td>
                    <td className="py-3"><span className={`badge ${item.is_active ? 'bg-success' : 'bg-secondary'}`}>{item.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td className="py-3 text-center">
                      <div className="btn-group btn-group-sm" role="group">
                        <button
                          onClick={() => handleEdit(item)}
                          className="btn btn-sm"
                          style={{ backgroundColor: '#2196F3', color: 'white', border: 'none' }}
                          title="Edit"
                        >
                          <i className="bi bi-pencil"></i> Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="btn btn-sm"
                          style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', marginLeft: '4px' }}
                          title="Delete"
                        >
                          <i className="bi bi-trash"></i> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                <h5 className="modal-title">{modalMode === 'create' ? 'Add Collaborator' : 'Edit Collaborator'}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Organization Name <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" name="organization_name" value={formData.organization_name} onChange={handleInputChange} required />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Type</label>
                      <select className="form-select" name="collaboration_type" value={formData.collaboration_type} onChange={handleInputChange}>
                        {collaborationTypes.map(type => <option key={type} value={type}>{type}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Country</label>
                      <input type="text" className="form-control" name="country" value={formData.country} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Email</label>
                      <input type="email" className="form-control" name="contact_email" value={formData.contact_email} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Website URL</label>
                      <input type="url" className="form-control" name="website_url" value={formData.website_url} onChange={handleInputChange} />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Logo Image</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleLogoChange}
                      />
                      {logoPreview && (
                        <div className="mt-2">
                          <img
                            src={logoPreview.startsWith('data:') || logoPreview.startsWith('http') ? logoPreview : `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${logoPreview}`}
                            alt="Logo preview"
                            style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain' }}
                            className="border rounded p-2"
                          />
                        </div>
                      )}
                      <small className="text-muted d-block mt-1">Supported formats: JPG, PNG, GIF, WebP, SVG (Max 5MB)</small>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Description</label>
                      <textarea className="form-control" name="description" value={formData.description} onChange={handleInputChange} rows="2"></textarea>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Collaboration Area</label>
                      <textarea className="form-control" name="collaboration_area" value={formData.collaboration_area} onChange={handleInputChange} rows="2"></textarea>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Start Date</label>
                      <input type="date" className="form-control" name="start_date" value={formData.start_date} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Display Order</label>
                      <input type="number" className="form-control" name="display_order" value={formData.display_order} onChange={handleInputChange} min="0" />
                    </div>
                    <div className="col-12">
                      <div className="form-check form-switch">
                        <input className="form-check-input" type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange} id="collab-active" />
                        <label className="form-check-label" htmlFor="collab-active">Active</label>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button
                  type="submit"
                  className="btn"
                  style={{ backgroundColor: '#6B8E23', color: 'white' }}
                  onClick={handleSubmit}
                  disabled={uploadingLogo}
                >
                  {uploadingLogo ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Uploading...
                    </>
                  ) : (
                    modalMode === 'create' ? 'Create' : 'Update'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollaboratorsManagement;
